/**
 * Format a raw postcode for details-sheet display (keeps full code).
 * BQA75B357  → BQA75 B357
 * BQA75B0357 → BQA75 B357  (strips leading zeros from delivery point)
 * Already-spaced codes returned as-is.
 */
import { composeBuildingAddressLine, flattenDedupeAddressSegments, joinAddressSegments } from './contributionAddress';
export function formatPostcodeFull(postcode: string | undefined | null): string {
  if (!postcode) return '';
  const raw = postcode.replace(/\s+/g, '').toUpperCase();
  // Find the 'B' that starts the delivery-point segment (last B followed by digits)
  const match = raw.match(/^([A-Z0-9]+?)(B\d+)$/);
  if (match) {
    const [, prefix, bPart] = match;
    // Strip leading zeros from the numeric part after B
    const delivery = bPart.slice(1).replace(/^0+/, '') || '0';
    return `${prefix} B${delivery}`;
  }
  // Fallback: insert space at position 5
  if (raw.length >= 6) return `${raw.slice(0, 5)} ${raw.slice(5)}`;
  return postcode;
}

/**
 * Format a Ghana postcode for short display (strips 2-char district prefix).
 * BQA75B0357 → A75 B357
 * LKA0SB0492 → A0S B492
 */
export function formatPostcodeDisplay(postcode: string | undefined | null): string {
  if (!postcode) return '';
  const raw = postcode.replace(/\s+/g, '').toUpperCase();
  // Strip 2-char district prefix for short display
  const stripped = raw.length >= 3 ? raw.slice(2) : raw;
  return formatPostcodeFull(stripped) || postcode;
}

/** Normalize postcode for substring comparison (ignore spaces/case). */
export function normalizePostcodeKey(postcode: string): string {
  return postcode.replace(/\s+/g, '').toUpperCase();
}

/** True when address already contains this postcode (formatted or raw). */
export function addressIncludesPostcode(
  address: string | undefined | null,
  postcode: string | undefined | null,
): boolean {
  if (!address || !postcode) return false;
  const key = normalizePostcodeKey(postcode);
  if (!key) return false;
  return normalizePostcodeKey(address).includes(key);
}

/** Ghana building postcode token embedded in address strings (e.g. GEA06B751). */
const EMBEDDED_POSTCODE_RE = /\b([A-Z]{2}[A-Z0-9]{3}B\d+)\b/gi;

/** Format any building-level postcodes found in free-text addresses. */
export function formatPostcodesInText(text: string | undefined | null): string {
  if (!text) return '';
  return text.replace(EMBEDDED_POSTCODE_RE, (match) => formatPostcodeFull(match) || match);
}

/**
 * Build native-format place address from structured parts, postcode, district, region.
 * Splits compound strings and dedupes repeated admin segments.
 */
export function buildNativePlaceAddress(parts: {
  name?: string;
  streetAddress?: string;
  addrStreet?: string;
  addrSuburb?: string;
  addrCity?: string;
  address?: string;
  district?: string;
  region?: string;
  postcode?: string | null;
  ghanaPostGps?: string | null;
  houseNumber?: string | null;
  frontageStreetAddress?: string | null;
}): string {
  const formattedPostcode = formatPostcodeFull(parts.postcode);

  const structuredLine = composeBuildingAddressLine({
    name: parts.name,
    street: parts.addrStreet || parts.streetAddress || '',
    suburb: parts.addrSuburb || '',
    city: parts.addrCity || '',
    district: parts.district || '',
    ghanaPostGps: parts.ghanaPostGps || '',
    houseNumber: parts.houseNumber || undefined,
    frontageStreetAddress: parts.frontageStreetAddress || undefined,
  });

  if (structuredLine) {
    let segments = flattenDedupeAddressSegments(structuredLine);
    if (formattedPostcode && !segments.some((s) => addressIncludesPostcode(s, formattedPostcode))) {
      segments.push(formattedPostcode);
    }
    return formatPostcodesInText(joinAddressSegments(segments, { ensureGhana: Boolean(formattedPostcode) }));
  }

  const base = parts.address || '';
  if (!base) {
    if (!parts.name) {
      return formattedPostcode ? formatPostcodesInText(`${formattedPostcode}, Ghana`) : '';
    }
    const nameOnly = composeBuildingAddressLine({
      name: parts.name,
      street: '',
      suburb: '',
      city: '',
      district: parts.district || '',
    });
    let segments = flattenDedupeAddressSegments(nameOnly);
    if (formattedPostcode && !segments.some((s) => addressIncludesPostcode(s, formattedPostcode))) {
      segments.push(formattedPostcode);
    }
    return formatPostcodesInText(joinAddressSegments(segments, { ensureGhana: true }));
  }

  const withName = parts.name
    ? joinAddressSegments(flattenDedupeAddressSegments(parts.name, base), { ensureGhana: false })
    : base;

  if (!formattedPostcode || addressIncludesPostcode(withName, formattedPostcode)) {
    return formatPostcodesInText(joinAddressSegments(flattenDedupeAddressSegments(withName), { ensureGhana: true }));
  }

  const segments = flattenDedupeAddressSegments(withName, formattedPostcode);
  return formatPostcodesInText(joinAddressSegments(segments, { ensureGhana: true }));
}

/**
 * Full address for clipboard — includes the place name when the detail view omits it.
 */
export function addressForClipboard(placeName: string | undefined, addressLine: string): string {
  const name = placeName?.trim() ?? '';
  const line = addressLine.trim();
  if (!line) return name;
  if (!name) return line;

  const lowerLine = line.toLowerCase();
  const lowerName = name.toLowerCase();
  if (
    lowerLine === lowerName
    || lowerLine.startsWith(`${lowerName},`)
    || lowerLine.startsWith(`${lowerName} `)
  ) {
    return line;
  }
  return `${name}, ${line}`;
}
